import type { FlairSpec } from '../../flair/engine';
import { type PaletteKey, PixelCanvas } from '../../lib';
import { grid, type Part } from '../body';
import type { Outfit } from '../outfits';
import { drawnLater } from './art';
import { type Ellipse, HEAD_AT, type HeadPicture, inEllipse, mask, type SetFlair, toneOf } from './mythic';

/**
 * The Plague Doctor set (ADR-0160 amended, mythic): a beaked leather mask under a wide black hat, an oilskin coat with a
 * bandolier of vials, and the Censer gun.
 *
 * The head is a plague doctor's mask (a mask over the whole head, tied on): a crow's beak of slate, short and hooked
 * (its signature: its top edge high and then bowing down to its point, its ridge lit pale all along so it parts from the
 * hood, a nostril by its root, a glint, a brass ring at its root; shaded round, no stripes down it, which read as a
 * drill's), two round lenses rimmed in brass glowing in the team's light (the far one
 * peeking over the beak), a hood of dark leather round it, lit along its top-left and violet along its other edge (the
 * miasma's light), and over it all a wide-brimmed black hat with the team's band and a brass buckle. Lying, the beak falls
 * across the chest (else the ground line would cut it off) and the hood spreads round the head. Its flair: a plume of
 * miasma always rising off the beak's tip (a violet wisp, its glow, puffs out of it that start pale, grow, drift back
 * over the hat and fade to purple), two moths circling the lenses, the lenses glowing. The coat is black oilskin with a
 * waxy sheen and a skirt over the thighs, the team's scarf wound at the neck and its end hanging, a bandolier of glass vials (violet, green) across the near shoulder, a leather belt with a brass
 * buckle; its flair: a brass censer (a stepped lid over a 7-px bowl, the miasma glowing through it) swung back and
 * forth on a pale chain from the belt, trailing a ribbon of violet smoke, a violet haze pooling round the feet, and the
 * coat's tail hanging behind.
 *
 * Rubric (`assetc mythic plague` at 5× and 8×, 1:1 on grass, lane and night, both teams, idle, run and dead, beside
 * Inferno): mask S5 R4 P4 L4 C4 A4 F4, coat S4 R4 P4 L4 C4 F4. Its gun, shots and crow: their own notes. Still weak: the
 * hood is dark on the night's dark (the hat's lit brim, the band, the lenses, the beak's pale ridge and the plume carry
 * it there); held, the Censer's vial hides the beak's lower half (its ridge and hook stay over it); the plume is squares on the art grid, puffs more than a smooth curl at 5×; the censer is a brass bell of
 * stacked squares (a chain's dots), not a round bowl.
 */

// --- Light -----------------------------------------------------------------------------------------------------------

/** The light on a surface whose normal is (nu, nv, nz) (u forward, v down, z toward us), from (lx, ly) and in front. */
const lightOn = (nu: number, nv: number, nz: number, lx: number, ly: number): number =>
  (nu * lx + nv * ly) * 0.737 + nz * 0.676;

// --- The hat ---------------------------------------------------------------------------------------------------------

/** The brim seen from above (u forward, v down from the head's middle), and how thick its front lip shows. */
const BRIM: Ellipse = [-2.5, -10, 24.5, 6.4];
const LIP = 2;
/** The crown: its middle, its top's ellipse, its foot on the brim, its half-width and how round its ends look. */
const CROWN = { u: -3, top: -18.6, foot: -10.6, rx: 10.5, ry: 2.6 } as const;
/** The band round the crown's foot (px tall), and the buckle on it (across the crown, -1 back … 1 front). */
const BAND = 3.2;
const BUCKLE = [0.18, 0.5] as const;

/** The hat at (u, v), or null: the crown's flat top, its side, the team's band at its foot, the brim round it. */
function hat(u: number, v: number, lx: number, ly: number): PaletteKey | null {
  const a = (u - CROWN.u) / CROWN.rx;
  if (Math.abs(a) <= 1) {
    const s = Math.sqrt(1 - a * a);
    const [back, front] = [CROWN.top - CROWN.ry * s, CROWN.top + CROWN.ry * s];
    const foot = CROWN.foot + CROWN.ry * s;
    // The flat top, lit along its back-left rim.
    if (v >= back && v <= front) return v < back + 1 || a < -0.86 ? '3' : '2';
    if (v > front && v <= foot) {
      const l = lightOn(a, 0, s, lx, ly);
      if (v > foot - BAND) {
        // The team's band, and its brass buckle.
        if (a >= BUCKLE[0] && a <= BUCKLE[1]) {
          const inner = a > BUCKLE[0] + 0.08 && a < BUCKLE[1] - 0.08 && v > foot - BAND + 1 && v < foot - 0.6;
          return inner ? '1' : l > 0.5 ? 'y' : 'Y';
        }
        return v < foot - BAND + 1 ? 'c' : l > 0.05 ? 'b' : 'B';
      }
      return l > 0.78 ? '3' : l > 0.3 ? '2' : '1';
    }
  }
  const r = inEllipse(u, v, BRIM);
  if (r <= 1) {
    // The brim's top: its back-left edge lit, in the crown's shade to its right.
    const toLight = ((u - BRIM[0]) / BRIM[2]) * lx + ((v - BRIM[1]) / BRIM[3]) * ly;
    if (r > 0.8 && toLight > 0.25) return '3';
    if (u > CROWN.u + CROWN.rx - 1 && u < CROWN.u + CROWN.rx + 6 && v < BRIM[1] + 2 && r < 0.7) return '1';
    return '2';
  }
  // Its front lip, in shade.
  if (v > BRIM[1] && inEllipse(u, v - LIP, BRIM) <= 1) return '1';
  return null;
}

// --- The mask --------------------------------------------------------------------------------------------------------

/** The hood round the head (wide: it hides the face's every row), the mask's face over it, its chin under the beak. */
const HOOD: Ellipse = [0, 2.5, 21, 13.5];
const FACE: Ellipse = [3, 2, 12.5, 9.5];
const JAW: Ellipse = [8.5, 11.5, 12, 6.5];
/** Lying, the hood spreads on the ground round the head (it hides the dead face's every row too): a rounded box. */
const FLAT_HOOD: Ellipse = [1, 1, 18, 22];
const inHood = (u: number, v: number, lying: boolean) =>
  lying
    ? ((u - FLAT_HOOD[0]) / FLAT_HOOD[2]) ** 4 + ((v - FLAT_HOOD[1]) / FLAT_HOOD[3]) ** 4 <= 1
    : inEllipse(u, v, HOOD) <= 1;
/**
 * The beak, a crow's, short and hooked: from its root to its tip (u), its top edge high for a while and then bowing down
 * to its point, its bottom edge running on to it (v, by how far along: 0 root … 1 tip).
 */
const ROOT = 2;
const TIP = 24;
const upper = (t: number) => -1 + 14.5 * t ** 2.8;
const lower = (t: number) => 13 + 0.5 * t ** 1.1;
/** Lying, the beak falls across the chest (it would point into the ground): turned this far round its root's middle. */
const FALLEN = 0.7;
const PIVOT = 6;
/** The lenses: [middle u, middle v, how wide (the far one foreshortened), glass radius, brass radius]. */
const LENSES = [
  [-4, 1.5, 1, 3.8, 5.5],
  [10.5, -1, 0.72, 3.2, 4.8],
] as const;

/** Where (u, v) is on the beak: how far along (0 root, 1 tip) and across (-1 its top edge … 1 its bottom one). */
function onBeak(u: number, v: number): { t: number; across: number } | null {
  if (u < ROOT || u > TIP) return null;
  const t = (u - ROOT) / (TIP - ROOT);
  const half = Math.max(0.5, (lower(t) - upper(t)) / 2);
  const across = (v - (upper(t) + lower(t)) / 2) / half;
  return Math.abs(across) <= 1 ? { t, across } : null;
}

/** The beak by the light on it: slate, its ridge pale. */
const CROW = [
  ['4', 0.72],
  ['3', 0.36],
  ['2', 0],
] as const;

/**
 * The beak's colour at a point of it, the light from (lx, ly): a brass ring round its root, its ridge lit all along (it
 * parts from the black hood), a nostril near its root, a glint short of its tip, slate by the light on a round cone (no
 * stripes down it: they read as a drill's), near-black under it.
 */
function beak(u: number, t: number, across: number, lx: number, ly: number): PaletteKey {
  if (u < ROOT + 2) return across < -0.5 ? 'y' : across < 0.45 ? 'Y' : 'U';
  if (across < -0.82) return '4';
  if (u > ROOT + 3 && u < ROOT + 6 && across > -0.6 && across < -0.3) return 'k';
  if (across > 0.8) return 'k';
  const l = lightOn(0.25, across, Math.sqrt(Math.max(0, 1 - across * across)), lx, ly);
  if (across < -0.5 && across > -0.78 && t > 0.1 && t < 0.5 && l > 0.78) return '5';
  return toneOf(l, CROW, 'k');
}

/** A lens at (u, v), or null: brass round glass glowing in the team's light, a glint toward the light. */
function lens(u: number, v: number, lx: number, ly: number, which: 0 | 1): PaletteKey | null {
  const [cu, cv, sx, glass, rim] = LENSES[which];
  const [du, dv] = [(u - cu) / sx, v - cv];
  const r = Math.hypot(du, dv);
  if (r > rim) return null;
  const toLight = (du * lx + dv * ly) / Math.max(r, 0.01);
  if (r > glass) return toLight > 0.4 ? 'y' : toLight > -0.45 ? 'Y' : 'U';
  // The glint: toward the light, a step in from the rim.
  if (Math.hypot(du - lx * glass * 0.45, dv - ly * glass * 0.45) < glass * 0.3) return 'w';
  return r < glass * 0.45 ? 'c' : toLight < -0.3 && r > glass * 0.75 ? 'B' : r > glass * 0.75 ? 'b' : 'c';
}

/** Leather by the light on it, rim lit where the outside is toward the light. */
const leather = (l: number, rim: boolean): PaletteKey =>
  rim ? (l > -0.2 ? '3' : '2') : l > 0.7 ? '3' : l > 0.2 ? '2' : '1';

const solid = (u: number, v: number, lying: boolean) =>
  inHood(u, v, lying) || inEllipse(u, v, FACE) <= 1 || inEllipse(u, v, JAW) <= 1 || onBeak(u, v) !== null;

const plagueMask: HeadPicture = (u, v, lx, ly, lying) => {
  const worn = hat(u, v, lx, ly);
  if (worn) return worn;
  const nearLens = lens(u, v, lx, ly, 0);
  if (nearLens) return nearLens;
  // Lying, the beak falls across the chest.
  const [bu, bv, blx, bly] = (() => {
    if (!lying) return [u, v, lx, ly];
    const [c, s] = [Math.cos(FALLEN), Math.sin(FALLEN)];
    const [du, dv] = [u - ROOT, v - PIVOT];
    return [ROOT + du * c + dv * s, PIVOT - du * s + dv * c, lx * c + ly * s, -lx * s + ly * c];
  })();
  const b = onBeak(bu, bv);
  if (b) return beak(bu, b.t, b.across, blx, bly);
  const farLens = lens(u, v, lx, ly, 1);
  if (farLens) return farLens;
  const part = inEllipse(u, v, FACE) <= 1 ? FACE : inEllipse(u, v, JAW) <= 1 ? JAW : HOOD;
  if (part === HOOD && !inHood(u, v, lying)) return null;
  const rim = !solid(u + lx * 1.5, v + ly * 1.5, lying) && !hat(u + lx * 1.5, v + ly * 1.5, lx, ly);
  // In the brim's shade, just under it.
  const shade = inEllipse(u, v - LIP - 2.5, BRIM) <= 1 ? 0.45 : 0;
  // The face's edge: a seam, a groove in the leather.
  if (part === FACE && inEllipse(u, v, FACE) > 0.84 && v > FACE[1] - 4) return '1';
  // The miasma's violet light on its edge turned from the day's.
  if (!solid(u - lx * 1.5, v - ly * 1.5, lying) && !hat(u - lx * 1.5, v - ly * 1.5, lx, ly)) return 'P';
  const [pu, pv] = [(u - part[0]) / part[2], (v - part[1]) / part[3]];
  // Past the hood's ellipse (spread on the ground), lit as its edge.
  const out = Math.max(1, Math.hypot(pu, pv));
  const [nu, nv] = [pu / out, pv / out];
  const l = lightOn(nu, nv, Math.sqrt(Math.max(0, 1 - nu * nu - nv * nv)), lx, ly);
  return leather(l + (part === FACE ? 0 : part === JAW ? -0.55 : -0.45) - shade, rim);
};

// --- The oilskin coat ------------------------------------------------------------------------------------------------

/** A vial standing on the bandolier: cork, glass neck, the liquid in it (lit, mid, deep). */
const vial = (liquid: readonly [PaletteKey, PaletteKey, PaletteKey]) => {
  const [lit, mid, deep] = liquid;
  return `
    .m.
    5w4
    ${lit}${mid}${deep}
    ${lit}${mid}${deep}
    ${mid}${mid}${deep}
  `;
};

/**
 * The coat standing (the frame's x 6-55, y 39-60): black oilskin with a waxy sheen, its sleeves, its skirt over the
 * thighs, the team's scarf wound at the neck (its end hanging down the near side), a bandolier of vials across the near
 * shoulder, a belt with a brass buckle.
 */
function coat(): Part {
  const [x0, y0] = [6, 39];
  const c = new PixelCanvas(50, 22);
  const at = (x: number, y: number, k: PaletteKey) => c.set(x - x0, y - y0, k);
  // The body and its skirt flaring over the thighs: lit down its near edge, a waxy sheen down it, a lit hem.
  for (let y = 44; y <= 59; y++) {
    const skirt = Math.max(0, y - 55);
    const [left, right] = [15 - Math.round(skirt * 0.5), 44 + Math.round(skirt * 0.3)];
    for (let x = left; x <= right; x++) {
      const sheen = x === left + 3 && y > 46 ? (y === 49 || y === 57 ? '4' : '3') : null;
      at(x, y, x === left ? '3' : (sheen ?? (x <= left + 2 || y === 59 ? '2' : x === 30 && skirt > 0 ? 'k' : '1')));
    }
  }
  // The sleeves: the near one lit along its top and edge, a sheen down it, a fold; the far one in shade.
  for (const [xa, far] of [
    [8, false],
    [43, true],
  ] as const)
    for (let y = 44; y <= 55; y++)
      for (let x = xa; x <= xa + 8; x++) {
        if (y === 44 && (x === xa || x === xa + 8)) continue;
        if (y >= 53) at(x, y, !far && x === xa + 3 && y === 54 ? 'Y' : far ? '1' : 'N');
        else if (far) at(x, y, y === 44 || x === xa ? '2' : '1');
        else at(x, y, x === xa || y === 44 ? '3' : x === xa + 3 ? '3' : x === xa + 6 ? '1' : '2');
      }
  // The belt, a brass buckle.
  for (let x = 15; x <= 44; x++) {
    at(x, 53, 'n');
    at(x, 54, 'N');
  }
  c.stamp(
    `
    yYY
    Y1U
    YUU
  `,
    24 - x0,
    52 - y0,
  );
  // The bandolier from the near shoulder down across the side, a vial in each loop.
  const strap = (x: number) => Math.round(46 + (x - 8) * 0.4);
  for (let x = 8; x <= 27; x++) {
    at(x, strap(x), 'n');
    at(x, strap(x) + 1, 'N');
  }
  for (const [x, liquid] of [
    [9, ['i', 'p', 'P']],
    [13, ['l', 'g', 'G']],
    [17, ['i', 'p', 'P']],
  ] as const)
    c.stamp(vial(liquid), x - x0, strap(x + 1) - 4 - y0);
  // The team's scarf wound at the neck, its end hanging down the near side, frayed.
  for (let x = 16; x <= 43; x++) {
    at(x, 43, x < 18 || x > 41 ? 'b' : 'c');
    at(x, 44, (x + 1) % 6 === 0 ? 'B' : 'b');
    at(x, 45, (x + 3) % 6 === 0 ? 'B' : 'b');
    at(x, 46, 'B');
  }
  for (let y = 46; y <= 53; y++) {
    const x = 20 + Math.round((y - 46) * 0.3);
    if (y === 53) {
      at(x, y, 'c');
      at(x + 2, y, 'b');
      continue;
    }
    at(x, y, 'c');
    at(x + 1, y, 'b');
    at(x + 2, y, 'b');
    at(x + 3, y, 'B');
  }
  return [c, x0, y0];
}

/** The coat lying (right of the legs, under the head): the belt at the waist (left), the bandolier's vials, the scarf. */
const DEAD_COAT = grid(`
  ....2233
  ...22333
  N2222cbB
  n2N3221b
  N2pN221b
  Y2PN221b
  Y22lN21b
  N22GN21B
  n222pN1b
  N222PN1b
  N2222N1b
  n22221Nb
  N222211b
  N2222111
  n222211.
  N2222111
  N2111111
  ....1111
`);

/** Tall black boots under the team's trousers. */
const OILSKIN_COAT = (): Outfit => ({ torso: coat(), dead: [DEAD_COAT, 24, 51], legs: { n: '2', N: '1' } });

// --- The Censer on every other gun -----------------------------------------------------------------------------------

/**
 * The Censer finish (the default gun is its own, `pollinator_plague`): near-black and dark slate where the steel was, dark
 * leather where the wood was, the brass kept brass.
 */
const CENSER = {
  '1': 'k',
  '2': '1',
  '3': '2',
  '4': '3',
  '5': '4',
  N: 'k',
  n: 'N',
  m: 'n',
  t: 'm',
  e: 't',
} as const;

// --- Its flair -------------------------------------------------------------------------------------------------------

/** Miasma, born to gone: pink at its heart, violet, purple as it thins. */
const MIASMA = [0xf6757a, 0xb55088, 0xb55088, 0x68386c] as const;
/** The plume's puffs: a pale pink edge while they are young (it shows on the lane and at night), violet, purple. */
const PLUME = [0xead4aa, 0xf6757a, 0xb55088, 0xb55088, 0x68386c] as const;
/** The haze pooling at the feet. */
const HAZE = [0xb55088, 0x68386c, 0x68386c] as const;
const BRASS = 0xfeae34;
const LINK = 0xc0cbdc;

/** The beak's tip in the head's frame: where the miasma comes off. */
const [TX, TY] = [TIP + HEAD_AT[0] - 0.5, lower(1) + HEAD_AT[1]];

/** The head's flair: the lenses' glow, the miasma off the beak's tip, moths round the lenses. */
const MASK_FLAIR: FlairSpec = [
  // The lenses glow in the team's light.
  {
    kind: 'pulse',
    at: { on: 'head', x: 28, y: 30 },
    r: [4, 6.5],
    alpha: [0.25, 0.5],
    period: 1.3,
    tone: 'teamLight',
    layer: 'front',
  },
  {
    kind: 'pulse',
    at: { on: 'head', x: 41, y: 29 },
    r: [3, 4.5],
    alpha: [0.25, 0.5],
    period: 1.3,
    tone: 'teamLight',
    layer: 'front',
  },
  // Miasma off the beak's tip: its light, a wisp curling up off it, and a plume of puffs rising out of the wisp and
  // drifting back over the hat, growing as they go and fading.
  {
    kind: 'pulse',
    at: { on: 'head', x: TX, y: TY - 1.5 },
    r: [5, 8],
    alpha: [0.2, 0.4],
    period: 1.7,
    tone: 0xb55088,
    layer: 'front',
  },
  {
    kind: 'flame',
    at: { on: 'head', x: TX, y: TY + 0.5 },
    h: 14,
    w: 2.8,
    tones: [0x68386c, 0xb55088, 0xf6757a],
    speed: 0.8,
    blown: 0.04,
    layer: 'front',
  },
  {
    kind: 'emit',
    at: { on: 'head', x: TX, y: TY - 9.5 },
    box: [3, 4],
    rate: 18,
    v: [-8, -2, -18, -12],
    carry: 0.8,
    g: -3,
    drag: 0.75,
    wobble: [2.2, 0.7],
    life: [1, 1.5],
    size: [2, 4],
    tones: PLUME,
    alpha: [1, 0],
    glow: 1.6,
    layer: 'front',
  },
  // Moths circling the lenses' light.
  {
    kind: 'orbit',
    at: { on: 'head', x: 34, y: 27 },
    rx: 16,
    ry: 6,
    tilt: -0.15,
    n: 2,
    period: 2.6,
    size: 3,
    tones: [0xead4aa, 0xc0cbdc],
    rough: 0.3,
    bob: [2, 5],
  },
];

const FLAIR: SetFlair = {
  head: MASK_FLAIR,
  outfit: [
    // The coat's tail hanging behind.
    {
      kind: 'chain',
      at: { on: 'torso', x: 14, y: 52 },
      n: 6,
      seg: 2.2,
      dir: Math.PI * 0.62,
      stiff: 3,
      g: 300,
      drag: 0.05,
      wind: 120,
      w: [12, 14, 15],
      shape: 'bars',
      tones: [0x3a4466, 0x262b44, 0x262b44, 0x262b44],
      rim: 0x181425,
      lit: 0x5a6988,
      tatter: 2,
      layer: 'back',
    },
    // Miasma pooling round the feet, rolling out along the ground.
    {
      kind: 'emit',
      at: { on: 'feet', x: 30, y: 68 },
      box: [30, 3],
      rate: 4,
      v: [-9, 9, -4, -1],
      carry: 0.7,
      g: -1,
      drag: 0.5,
      wobble: [1.5, 0.6],
      life: [1, 1.6],
      size: [3, 5],
      tones: HAZE,
      alpha: [0.75, 0],
      glow: 1.4,
      layer: 'back',
    },
    // The censer on its chain from the belt: a brass bowl under a stepped lid, the miasma glowing through its holes,
    // swung back and forth, trailing a ribbon of violet smoke.
    {
      kind: 'chain',
      at: { on: 'torso', x: 14, y: 54 },
      n: 6,
      seg: 2,
      dir: Math.PI * 0.54,
      stiff: 3,
      g: 380,
      drag: 0.1,
      sway: [0.32, 0.65, 0],
      w: [1, 1, 7],
      shape: 'dots',
      tones: [LINK, LINK, LINK, LINK, LINK, LINK, 0xd77643, BRASS, BRASS, BRASS],
      lit: 0xf6757a,
      shed: {
        rate: 10,
        v: [-4, 4, -12, -5],
        carry: 0.7,
        g: -4,
        drag: 0.6,
        wobble: [1.5, 1],
        life: [0.8, 1.3],
        size: [1, 4],
        tones: PLUME,
        alpha: [0.95, 0],
        glow: 1.6,
      },
      layer: 'front',
    },
  ],
  gun: [
    // The vial's light, and smoke out of the vent.
    {
      kind: 'pulse',
      at: { on: 'gun', x: 32, y: 9 },
      r: [5, 7],
      alpha: [0.12, 0.28],
      period: 0.9,
      tone: 0xb55088,
      layer: 'gun',
    },
    {
      kind: 'emit',
      at: { on: 'gun', x: 48, y: 9 },
      box: [3, 1],
      rate: 6,
      v: [-3, 3, -16, -8],
      g: -6,
      drag: 0.6,
      wobble: [1.5, 1.2],
      life: [0.6, 1.1],
      size: [1, 3],
      tones: MIASMA,
      alpha: [0.9, 0],
      layer: 'gun',
    },
  ],
};

/** The Plague Doctor set (ADR-0160 amended). */
export const PLAGUE = drawnLater({ head: () => mask(plagueMask), outfit: OILSKIN_COAT, gun: CENSER, flair: FLAIR });
